/**
 * Fume canonical transcript `T`.
 *
 * Binds the RFC 9382 SPAKE2 transcript together with the Fume session
 * parameters (ciphersuite, rendezvous nonce, TTL, and the manifest / fragment /
 * receipt-device placeholders that later slices fill in), plus the optional
 * key-confirmation tags.
 *
 * Framing reuses the existing `hashTranscript()` helper: SHA-256 over, for each
 * ordered field, `uint64be(length) || bytes` (BIG-ENDIAN). This is deliberately
 * a DIFFERENT framing from the RFC 9382 TT (little-endian); the SPAKE2 TT is
 * committed here as an opaque first field, so both framings coexist without
 * being mixed.
 */
import { FumeCryptoError } from './errors.js';
import { hashTranscript } from './key-schedule.js';
import { SELECTOR_NONCE_SIZE } from './constants.js';
const utf8 = new TextEncoder();
const PLACEHOLDER_SIZE = 32;
const CONFIRMATION_SIZE = 32;
const MAX_TTL_SECONDS = 2 ** 32 - 1;
function requireBytes(value, name) {
    if (!(value instanceof Uint8Array)) {
        throw new FumeCryptoError(`${name} must be a Uint8Array`);
    }
}
function requireExact(value, expected, name) {
    requireBytes(value, name);
    if (value.length !== expected) {
        throw new FumeCryptoError(`${name} must be exactly ${expected} bytes, got ${value.length}`);
    }
}
/** uint64be(x) as an 8-byte big-endian field (matches selector.ts framing). */
function ttlField(ttlSeconds) {
    if (!Number.isInteger(ttlSeconds) || ttlSeconds < 0 || ttlSeconds > MAX_TTL_SECONDS) {
        throw new FumeCryptoError(`ttlSeconds must be an integer in 0..${MAX_TTL_SECONDS}`);
    }
    const out = new Uint8Array(8);
    new DataView(out.buffer).setBigUint64(0, BigInt(ttlSeconds), false /* big-endian */);
    return out;
}
/**
 * Canonical Fume transcript hash. Hashes the ordered fields with `uint64be`
 * length framing, identical to `hashTranscript()`. Strings are UTF-8 encoded.
 * Confirmation tags are appended only when present, so the no-confirmation and
 * with-confirmation transcripts hash to distinct values.
 */
export function assembleFumeTranscript(input) {
    if (input == null) {
        throw new FumeCryptoError('transcript input is required');
    }
    requireBytes(input.spake2Transcript, 'spake2Transcript');
    if (typeof input.cipherSuiteId !== 'string') {
        throw new FumeCryptoError('cipherSuiteId must be a string');
    }
    requireExact(input.nonce, SELECTOR_NONCE_SIZE, 'nonce');
    requireExact(input.manifestId, PLACEHOLDER_SIZE, 'manifestId');
    requireExact(input.fragmentScheduleParams, PLACEHOLDER_SIZE, 'fragmentScheduleParams');
    requireExact(input.receiptDeviceKeyA, PLACEHOLDER_SIZE, 'receiptDeviceKeyA');
    requireExact(input.receiptDeviceKeyB, PLACEHOLDER_SIZE, 'receiptDeviceKeyB');
    const fields = [
        input.spake2Transcript,
        utf8.encode(input.cipherSuiteId),
        input.nonce,
        ttlField(input.ttlSeconds),
        input.manifestId,
        input.fragmentScheduleParams,
        input.receiptDeviceKeyA,
        input.receiptDeviceKeyB,
    ];
    if (input.confirmationA !== undefined) {
        requireExact(input.confirmationA, CONFIRMATION_SIZE, 'confirmationA');
        fields.push(input.confirmationA);
    }
    if (input.confirmationB !== undefined) {
        requireExact(input.confirmationB, CONFIRMATION_SIZE, 'confirmationB');
        fields.push(input.confirmationB);
    }
    return { hash: hashTranscript(fields) };
}
//# sourceMappingURL=transcript.js.map